// A text box's frame. A box is stored as its top-left anchor (`x`, `y`, world
// mm, Y-up), its size (`w` along its own x, `h` downwards along its own y) and an
// optional `rotation` (degrees CCW) about that anchor. Every consumer — drawing,
// hit test, handles, resize, snap, marquee, bounds, DXF — reads the box through
// these helpers, so a rotated box is the same box everywhere. Rotating about the
// ANCHOR (not the centre) is what keeps a resize or the auto-grow trivial: the
// top-left corner stays where it is. The knob and the panel still turn the box
// about its centre, through `rotateTextBoxTo`, which moves the anchor to match.
// Pure and DOM-free (unit-tested).

import type { Vec2 } from '../shared/types';

export interface TextBoxLike { x: number; y: number; w: number; h: number; rotation?: number }

/** The box's axes in world space: `u` along its top edge (left → right) and `v`
 *  down its left edge (top → bottom). */
export function textBoxAxes(t: TextBoxLike): { u: Vec2; v: Vec2 } {
  const r = ((t.rotation ?? 0) * Math.PI) / 180;
  const c = Math.cos(r), s = Math.sin(r);
  return { u: { x: c, y: s }, v: { x: s, y: -c } };
}

/** The world point at box-local (`lx` right, `ly` down from the top-left). */
export function textBoxToWorld(t: TextBoxLike, lx: number, ly: number): Vec2 {
  const { u, v } = textBoxAxes(t);
  return { x: t.x + u.x * lx + v.x * ly, y: t.y + u.y * lx + v.y * ly };
}

/** A world point in the box's own frame (`x` right, `y` down from the top-left). */
export function textBoxLocal(t: TextBoxLike, p: Vec2): Vec2 {
  const { u, v } = textBoxAxes(t);
  const dx = p.x - t.x, dy = p.y - t.y;
  return { x: dx * u.x + dy * u.y, y: dx * v.x + dy * v.y };
}

/** Top-left, top-right, bottom-right, bottom-left, in world space. */
export function textBoxCorners(t: TextBoxLike): Vec2[] {
  return [textBoxToWorld(t, 0, 0), textBoxToWorld(t, t.w, 0), textBoxToWorld(t, t.w, t.h), textBoxToWorld(t, 0, t.h)];
}

export function textBoxCentre(t: TextBoxLike): Vec2 {
  return textBoxToWorld(t, t.w / 2, t.h / 2);
}

export function textBoxContains(t: TextBoxLike, p: Vec2): boolean {
  const l = textBoxLocal(t, p);
  return l.x >= 0 && l.x <= t.w && l.y >= 0 && l.y <= t.h;
}

/** Turn the box to `deg` about its CENTRE (the knob's and the panel's pivot),
 *  moving the anchor so the centre stays put. */
export function rotateTextBoxTo(t: TextBoxLike, deg: number): void {
  const c = textBoxCentre(t);
  const norm = ((deg % 360) + 360) % 360;
  t.rotation = norm;
  const back = textBoxToWorld({ ...t, x: 0, y: 0 }, t.w / 2, t.h / 2);
  t.x = c.x - back.x;
  t.y = c.y - back.y;
  if (!t.rotation) delete t.rotation;
}

/** Put the box's centre at `c`, keeping its size and rotation. */
export function moveTextBoxCentreTo(t: TextBoxLike, c: Vec2): void {
  const back = textBoxToWorld({ ...t, x: 0, y: 0 }, t.w / 2, t.h / 2);
  t.x = c.x - back.x;
  t.y = c.y - back.y;
}
